import React, { useState, useEffect, useRef } from "react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import api from "../lib/api";
import { useLiveMetrics } from "../hooks/useLiveMetrics";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Meter { id: number; serial_number: string; unit_number: string; property_name: string; status: string; valve_open: boolean; }
interface DailyUsage { date: string; units_consumed: string; peak_flow_rate: string; }
interface Payment { id: number; amount: string; created_at: string; units_allocated: string | null; status: string; }
interface TokenBalance { balance: string; unit_label: string; }

const fmt = (n: number, d = 1) =>
  n.toLocaleString("en-KE", { minimumFractionDigits: d, maximumFractionDigits: d });

const shortDate = (s: string) =>
  new Date(s).toLocaleDateString("en-KE", { day: "numeric", month: "short" });

const shortMonth = (s: string) =>
  new Date(s).toLocaleDateString("en-KE", { month: "short", year: "2-digit" });

const PIE_COLORS = ["#0ea5e9", "#6366f1", "#10b981", "#f59e0b"];

// ── Live dot ──────────────────────────────────────────────────────────────────
function LiveDot({ connected }: { connected: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
      <span className={`w-2 h-2 rounded-full ${connected ? "bg-emerald-400 animate-pulse" : "bg-gray-300"}`} />
      <span className={connected ? "text-emerald-600" : "text-gray-400"}>
        {connected ? "LIVE" : "Offline"}
      </span>
    </span>
  );
}

// ── Tooltip ───────────────────────────────────────────────────────────────────
function ChartTip({ active, payload, label, prefix = "", suffix = "" }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 border border-white/10 rounded-xl px-3 py-2.5 shadow-xl text-xs">
      <p className="text-gray-400 mb-1.5 font-medium">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} className="flex items-center gap-2 text-white">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-gray-400">{p.name}:</span>
          <span className="font-bold">{prefix}{fmt(Number(p.value), 2)}{suffix}</span>
        </p>
      ))}
    </div>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon, color, live }: {
  label: string; value: string; sub?: string;
  icon: React.ReactNode; color: string; live?: boolean;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4 hover:shadow-md transition-shadow">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-widest">{label}</p>
          {live && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
        </div>
        <p className="text-2xl font-bold text-gray-800 leading-tight mt-0.5">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

// ── Range tabs ────────────────────────────────────────────────────────────────
function RangeTabs({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const tabs = ["Daily", "Monthly"];
  return (
    <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
      {tabs.map((t) => (
        <button
          key={t}
          onClick={() => onChange(t.toLowerCase())}
          className={`px-3 py-1 rounded-md text-xs font-semibold transition-all ${
            value === t.toLowerCase() ? "bg-white text-blue-600 shadow-sm" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function TenantAnalyticsPage() {
  const [loading,      setLoading]      = useState(true);
  const [range,        setRange]        = useState("monthly");
  const [meters,       setMeters]       = useState<Meter[]>([]);
  const [selectedId,   setSelectedId]   = useState<number | null>(null);
  const [dailyUsage,   setDailyUsage]   = useState<DailyUsage[]>([]);
  const [payments,     setPayments]     = useState<Payment[]>([]);
  const [balance,      setBalance]      = useState<TokenBalance | null>(null);

  // ── WebSocket live stream ──────────────────────────────────────────────────
  const { reading, history, connected } = useLiveMetrics(selectedId);

  // ── Fetch data on mount ────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      api.get("/meters/"),
      api.get("/analytics/usage/"),
      api.get("/payments/history/"),
    ]).then(([mRes, uRes, pRes]) => {
      const mList: Meter[] = mRes.data.results ?? mRes.data ?? [];
      setMeters(mList);
      if (mList.length > 0) setSelectedId(mList[0].id);
      setDailyUsage(uRes.data.results ?? uRes.data ?? []);
      setPayments(pRes.data.results ?? pRes.data ?? []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Fetch balance when meter changes
  useEffect(() => {
    if (!selectedId) return;
    api.get(`/tokens/balance/?meter=${selectedId}`)
      .then((r) => setBalance(r.data?.results?.[0] ?? r.data ?? null))
      .catch(() => {});
  }, [selectedId]);

  // ── Derived chart data ─────────────────────────────────────────────────────
  // Daily usage → chart points
  const dailyChart = dailyUsage.slice(-30).map((d) => ({
    date:  shortDate(d.date),
    usage: parseFloat(d.units_consumed),
    peak:  parseFloat(d.peak_flow_rate),
  }));

  // Group daily by month for monthly view
  const monthlyMap: Record<string, { usage: number; spend: number }> = {};
  dailyUsage.forEach((d) => {
    const key = shortMonth(d.date);
    if (!monthlyMap[key]) monthlyMap[key] = { usage: 0, spend: 0 };
    monthlyMap[key].usage += parseFloat(d.units_consumed);
  });
  payments.forEach((p) => {
    const key = shortMonth(p.created_at);
    if (!monthlyMap[key]) monthlyMap[key] = { usage: 0, spend: 0 };
    monthlyMap[key].spend += parseFloat(p.amount);
  });
  const monthlyChart = Object.entries(monthlyMap)
    .slice(-12)
    .map(([month, v]) => ({ month, ...v }));

  // Hourly distribution from live history
  const hourlyMap: Record<number, number> = {};
  history.forEach((p) => {
    const h = new Date(p.timestamp).getHours();
    hourlyMap[h] = (hourlyMap[h] || 0) + p.flow_rate;
  });
  const hourlyChart = Array.from({ length: 24 }, (_, h) => ({
    hour: `${String(h).padStart(2, "0")}:00`,
    flow: parseFloat((hourlyMap[h] ?? 0).toFixed(3)),
  }));

  // Usage by time-of-day pie (from hourly)
  const todBuckets = [
    { name: "Morning 5–9am",    value: [5,6,7,8,9] },
    { name: "Daytime 10am–4pm", value: [10,11,12,13,14,15,16] },
    { name: "Evening 5–9pm",    value: [17,18,19,20,21] },
    { name: "Night 10pm–4am",   value: [22,23,0,1,2,3,4] },
  ].map((b) => ({
    name:  b.name,
    value: parseFloat(b.value.reduce((s, h) => s + (hourlyMap[h] ?? 0), 0).toFixed(3)),
  }));
  const todTotal = todBuckets.reduce((s, b) => s + b.value, 0);
  const todPie = todBuckets.map((b) => ({
    ...b,
    pct: todTotal > 0 ? Math.round((b.value / todTotal) * 100) : 25,
  }));

  // KPIs
  const totalUsage  = dailyChart.reduce((s, d) => s + d.usage, 0);
  const totalSpend  = payments.reduce((s, p) => s + parseFloat(p.amount), 0);
  const avgMonthly  = monthlyChart.length ? totalUsage / monthlyChart.length : 0;
  const peakDay     = [...dailyChart].sort((a, b) => b.usage - a.usage)[0];
  const currentFlow = reading ? parseFloat(reading.flow_rate) : 0;
  const currentPres = reading ? parseFloat(reading.pressure)  : 0;
  const currentCum  = reading ? parseFloat(reading.cumulative): 0;
  const tokenBal    = balance  ? parseFloat(balance.balance)  : 0;

  const selectedMeter = meters.find((m) => m.id === selectedId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-5 space-y-6 max-w-[1400px] mx-auto">

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800">My Analytics</h1>
          <p className="text-sm text-gray-400 mt-0.5">Live consumption · spending · pressure · token balance</p>
        </div>
        <div className="flex items-center gap-3">
          <LiveDot connected={connected} />
          {meters.length > 0 && (
            <select
              value={selectedId ?? ""}
              onChange={(e) => setSelectedId(Number(e.target.value))}
              className="text-xs bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {meters.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.serial_number} · {m.unit_number}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* ── Live token + meter status banner ────────────────── */}
      {reading && (
        <div className="bg-gradient-to-r from-blue-600 to-teal-500 rounded-2xl p-5 text-white">
          <div className="flex flex-wrap gap-6 items-center justify-between">
            <div>
              <p className="text-blue-100 text-xs font-semibold uppercase tracking-wider">Token Balance</p>
              <p className="text-3xl font-black mt-0.5">{fmt(tokenBal, 2)} <span className="text-lg font-medium text-blue-200">units</span></p>
              {tokenBal < 5 && <p className="text-yellow-200 text-xs mt-1 font-medium">⚠️ Low balance — top up soon</p>}
            </div>
            <div className="flex gap-6 flex-wrap">
              <div>
                <p className="text-blue-100 text-xs font-semibold">Flow Rate</p>
                <p className="text-xl font-bold">{fmt(currentFlow, 3)} <span className="text-sm text-blue-200">L/h</span></p>
              </div>
              <div>
                <p className="text-blue-100 text-xs font-semibold">Pressure</p>
                <p className={`text-xl font-bold ${currentPres >= 8 || currentPres <= 0.5 ? "text-yellow-200" : ""}`}>
                  {fmt(currentPres, 2)} <span className="text-sm text-blue-200">bar</span>
                </p>
              </div>
              <div>
                <p className="text-blue-100 text-xs font-semibold">Cumulative</p>
                <p className="text-xl font-bold">{fmt(currentCum, 2)} <span className="text-sm text-blue-200">L</span></p>
              </div>
              <div>
                <p className="text-blue-100 text-xs font-semibold">Valve</p>
                <p className={`text-xl font-bold ${reading.valve_open ? "text-emerald-200" : "text-red-200"}`}>
                  {reading.valve_open ? "Open" : "Closed"}
                </p>
              </div>
            </div>
            <span className="flex items-center gap-1.5 text-xs text-blue-100">
              <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
              LIVE
            </span>
          </div>
        </div>
      )}

      {/* ── KPI strip ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Usage (30d)" value={`${fmt(totalUsage, 1)} L`}
          sub="All daily readings combined"
          color="bg-blue-50" live
          icon={<svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6 8 4 12 4 15a8 8 0 0016 0c0-3-2-7-8-13z"/></svg>}
        />
        <StatCard
          label="Total Spent" value={`KES ${fmt(totalSpend, 0)}`}
          sub={`${payments.length} payments`}
          color="bg-emerald-50"
          icon={<svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20M6 15h4" strokeLinecap="round"/></svg>}
        />
        <StatCard
          label="Avg Daily Usage" value={`${fmt(avgMonthly / 30, 2)} L`}
          sub="Based on available data"
          color="bg-violet-50"
          icon={<svg className="w-5 h-5 text-violet-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" d="M3 17l4-8 4 5 3-3 4 6M3 21h18"/></svg>}
        />
        <StatCard
          label="Peak Day" value={peakDay ? `${fmt(peakDay.usage, 1)} L` : "—"}
          sub={peakDay ? peakDay.date : "No data yet"}
          color="bg-amber-50"
          icon={<svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>}
        />
      </div>

      {/* ── Live flow + pressure charts ───────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Live flow scrolling line */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-sm font-bold text-gray-800">Live Flow Rate</h2>
              <p className="text-xs text-gray-400 mt-0.5">{history.length} readings · auto-updates every 30 s</p>
            </div>
            <LiveDot connected={connected} />
          </div>
          {history.length === 0 ? (
            <EmptyState icon="💧" message="Waiting for live meter data…" />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="time" tick={{ fontSize: 9, fill: "#94a3b8" }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" L/h" width={60} />
                <Tooltip content={<ChartTip suffix=" L/h" />} />
                <Line type="monotone" dataKey="flow_rate" name="Flow" stroke="#0ea5e9" strokeWidth={2} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Live cumulative consumption */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-sm font-bold text-gray-800">Live Cumulative Consumption</h2>
              <p className="text-xs text-gray-400 mt-0.5">Total litres through your meter this session</p>
            </div>
            <LiveDot connected={connected} />
          </div>
          {history.length === 0 ? (
            <EmptyState icon="📈" message="Waiting for live meter data…" />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={history}>
                <defs>
                  <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#10b981" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="time" tick={{ fontSize: 9, fill: "#94a3b8" }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" L" width={60} />
                <Tooltip content={<ChartTip suffix=" L" />} />
                <Area type="monotone" dataKey="cumulative" name="Cumulative" stroke="#10b981" strokeWidth={2} fill="url(#cumGrad)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Historical consumption (daily / monthly toggle) ───── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-sm font-bold text-gray-800">Water Consumption History</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {range === "daily" ? "Last 30 days" : "Last 12 months"} · from meter readings
              </p>
            </div>
            <RangeTabs value={range} onChange={setRange} />
          </div>
          {(range === "daily" ? dailyChart : monthlyChart).length === 0 ? (
            <EmptyState icon="📊" message="No historical usage data yet" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={(range === "daily" ? dailyChart : monthlyChart) as any[]}>
                <defs>
                  <linearGradient id="histGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey={range === "daily" ? "date" : "month"} tick={{ fontSize: 10, fill: "#94a3b8" }} interval={range === "daily" ? 4 : 0} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" L" width={56} />
                <Tooltip content={<ChartTip suffix=" L" />} />
                <Area type="monotone" dataKey="usage" name="Usage" stroke="#3b82f6" strokeWidth={2} fill="url(#histGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Usage by time of day pie */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="mb-4">
            <h2 className="text-sm font-bold text-gray-800">Usage by Time of Day</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {connected ? "From live session data" : "Typical pattern"}
            </p>
          </div>
          <ResponsiveContainer width="100%" height={150}>
            <PieChart>
              <Pie data={todPie} cx="50%" cy="50%" innerRadius={42} outerRadius={65}
                paddingAngle={3} dataKey="pct">
                {todPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v: any) => [`${v}%`, ""]}
                contentStyle={{ background: "#1e293b", border: "none", borderRadius: 10, fontSize: 12 }}
                itemStyle={{ color: "#e2e8f0" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-3 space-y-1.5">
            {todPie.map((c, i) => (
              <div key={c.name} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: PIE_COLORS[i] }} />
                <span className="text-gray-500 flex-1 truncate">{c.name}</span>
                <span className="font-semibold text-gray-700">{c.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Monthly spend + hourly live pattern ──────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

        {/* Monthly spend bar */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="mb-4">
            <h2 className="text-sm font-bold text-gray-800">Monthly Spending (KES)</h2>
            <p className="text-xs text-gray-400 mt-0.5">M-Pesa token top-ups per month</p>
          </div>
          {monthlyChart.length === 0 ? (
            <EmptyState icon="💳" message="No payment history yet" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlyChart} barSize={14}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" KES" width={64} />
                <Tooltip content={<ChartTip prefix="KES " />} />
                <Bar dataKey="spend" name="Spend" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Hourly distribution from live history */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-sm font-bold text-gray-800">Hourly Flow Pattern</h2>
              <p className="text-xs text-gray-400 mt-0.5">Average flow rate by hour (live session)</p>
            </div>
            <LiveDot connected={connected} />
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={hourlyChart} barSize={8}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="hour" tick={{ fontSize: 9, fill: "#94a3b8" }} interval={3} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" L/h" width={56} />
              <Tooltip content={<ChartTip suffix=" L/h" />} />
              <Bar dataKey="flow" name="Flow" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Consumption vs spend dual bar ─────────────────────── */}
      {monthlyChart.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="mb-4">
            <h2 className="text-sm font-bold text-gray-800">Consumption vs Spend</h2>
            <p className="text-xs text-gray-400 mt-0.5">Compare litres used against amount paid each month</p>
          </div>
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={monthlyChart} barSize={12}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#94a3b8" }} />
              <YAxis yAxisId="l" tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" L" width={52} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: "#94a3b8" }} unit=" KES" width={64} />
              <Tooltip content={<ChartTip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12, color: "#64748b" }} />
              <Bar yAxisId="l" dataKey="usage" name="Usage (L)"    fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="r" dataKey="spend" name="Spend (KES)"  fill="#6366f1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Smart insights ───────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="mb-4">
          <h2 className="text-sm font-bold text-gray-800">Smart Insights</h2>
          <p className="text-xs text-gray-400 mt-0.5">Based on your real usage data</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <InsightCard icon="💧" color="bg-blue-50 border-blue-100"
            title="Daily Average"
            body={`You use approximately ${fmt(totalUsage / Math.max(dailyChart.length, 1), 2)} L per day. ${
              totalUsage / Math.max(dailyChart.length, 1) > 150
                ? "Consider checking for running taps or leaks."
                : "Your usage is within a healthy range."
            }`}
          />
          <InsightCard icon="💳" color="bg-emerald-50 border-emerald-100"
            title="Spending Pattern"
            body={`You've spent KES ${fmt(totalSpend, 0)} across ${payments.length} top-up${payments.length !== 1 ? "s" : ""}. ${
              payments.length > 0
                ? `Average top-up: KES ${fmt(totalSpend / payments.length, 0)}.`
                : "Set up auto top-up so you never run out."
            }`}
          />
          <InsightCard icon={tokenBal < 5 ? "⚠️" : "✅"} color={tokenBal < 5 ? "bg-amber-50 border-amber-100" : "bg-emerald-50 border-emerald-100"}
            title="Token Status"
            body={tokenBal < 5
              ? `Your balance is critically low (${fmt(tokenBal, 2)} units). Top up now to avoid valve closure.`
              : `Balance is healthy at ${fmt(tokenBal, 2)} units. Based on your daily usage, that's approximately ${fmt(tokenBal / Math.max(totalUsage / Math.max(dailyChart.length, 1), 0.1), 0)} days remaining.`
            }
          />
        </div>
      </div>

    </div>
  );
}

function InsightCard({ icon, color, title, body }: { icon: string; color: string; title: string; body: string }) {
  return (
    <div className={`rounded-xl border p-4 ${color}`}>
      <div className="text-2xl mb-2">{icon}</div>
      <p className="text-xs font-bold text-gray-800 mb-1">{title}</p>
      <p className="text-xs text-gray-500 leading-relaxed">{body}</p>
    </div>
  );
}

function EmptyState({ icon, message }: { icon: string; message: string }) {
  return (
    <div className="h-[200px] flex flex-col items-center justify-center gap-2 text-gray-300">
      <span className="text-3xl">{icon}</span>
      <p className="text-sm text-gray-400">{message}</p>
    </div>
  );
}
